/**
 * Platform Overview — focused composition replacing the old 2160-line SuperAdminPage.
 * Uses independent panel states so one failed request doesn't blank the whole page.
 * Pending onboarding panel includes clickable review drawer with Approve/Reject.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BuildingOffice2Icon,
  UsersIcon,
  DocumentCheckIcon,
  ClipboardDocumentListIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  EnvelopeIcon,
  XMarkIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { usePlatformOverview } from '../features/super-admin/hooks/usePlatformOverview';
import { approveRequest, rejectRequest, precheckRequest, type PrecheckResult } from '../features/super-admin/api/superAdminApi';
import { LoadingState, ErrorState, EmptyState } from '../features/super-admin/components/shared';
import type { RegistrationRequest } from '../features/super-admin/types';

const PlatformOverviewPage: React.FC = () => {
  const { overview, requests, health, loading, error, refresh } = usePlatformOverview();
  const [reviewRequest, setReviewRequest] = useState<RegistrationRequest | null>(null);

  if (loading) {
    return <LoadingState message="Loading platform overview..." />;
  }

  if (error && !overview) {
    return (
      <div>
        <h1 className="text-xl font-bold text-slate-800 mb-4">Platform Overview</h1>
        <ErrorState message={error} onRetry={refresh} />
      </div>
    );
  }

  const totalUsers = overview?.users?.reduce((sum, r) => sum + r.count, 0) ?? 0;
  const activeUsers = overview?.users?.reduce((sum, r) => sum + r.active, 0) ?? 0;
  const superAdminCount = overview?.users?.find((r) => r.role === 'super_admin')?.count ?? 0;
  const pendingApprovals = overview?.pendingApprovals ?? 0;
  const auditEventsToday = overview?.auditEventsToday ?? 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Platform Overview</h1>
          <p className="text-sm text-slate-500 mt-0.5">eSanza platform health and activity</p>
        </div>
        <button
          type="button"
          onClick={refresh}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-slate-600 hover:text-slate-800 border border-slate-200 rounded-lg hover:bg-white transition-colors"
        >
          <ArrowPathIcon className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPICard icon={<UsersIcon className="w-5 h-5" />} label="Platform Users" value={totalUsers} sub={`${activeUsers} active`} color="emerald" />
        <KPICard icon={<BuildingOffice2Icon className="w-5 h-5" />} label="Pending Onboarding" value={pendingApprovals} sub="awaiting review" color="blue" />
        <KPICard icon={<ClipboardDocumentListIcon className="w-5 h-5" />} label="Audit Events Today" value={auditEventsToday} sub="last 24 hours" color="amber" />
        <KPICard icon={<DocumentCheckIcon className="w-5 h-5" />} label="Super Admins" value={superAdminCount} sub="platform accounts" color="slate" />
      </div>

      {/* Content panels */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pending onboarding queue — with review action */}
        <Panel title="Pending Onboarding" icon={<DocumentCheckIcon className="w-4 h-4" />}>
          {requests.length === 0 ? (
            <EmptyState title="No pending requests" subtitle="All onboarding requests have been processed." />
          ) : (
            <div className="divide-y divide-slate-100">
              {requests.map((req) => (
                <button
                  key={req.id}
                  type="button"
                  onClick={() => setReviewRequest(req)}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors text-left"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">
                      {req.company_name || req.contact_name || `Request #${req.id}`}
                    </p>
                    <p className="text-xs text-slate-500 truncate">
                      {req.contact_email || ''}{req.country ? ` · ${req.country}` : ''}
                    </p>
                  </div>
                  <span className="ml-3 shrink-0 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                    Review &rarr;
                  </span>
                </button>
              ))}
            </div>
          )}
        </Panel>

        {/* System health */}
        <Panel title="System Health" icon={<ExclamationTriangleIcon className="w-4 h-4" />}>
          <div className="px-4 py-4 space-y-3">
            <HealthRow label="API Status" healthy={health?.status === 'healthy'} />
            <HealthRow label="Database" healthy={health?.database === 'connected'} />
            {health?.version && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">Backend Version</span>
                <span className="text-slate-700 font-medium">{health.version.backend_version || 'unknown'}</span>
              </div>
            )}
            {health?.version?.environment && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">Environment</span>
                <span className="text-slate-700 font-medium capitalize">{health.version.environment}</span>
              </div>
            )}
            {!health && (
              <p className="text-xs text-amber-600">Health check unavailable. The API may be unreachable.</p>
            )}
          </div>
        </Panel>

        {/* Quick links */}
        <Panel title="Quick Actions" icon={<EnvelopeIcon className="w-4 h-4" />}>
          <div className="p-4 space-y-2">
            <QuickLink to="/dashboard/company-management" label="Manage Organisations" />
            <QuickLink to="/dashboard/platform-users" label="Manage Platform Users" />
            <QuickLink to="/dashboard/subscriptions" label="Subscriptions" />
            <QuickLink to="/dashboard/website-control" label="Website Inbox" />
            <QuickLink to="/dashboard/audit-logs" label="Audit Logs" />
            <QuickLink to="/dashboard/platform-settings" label="Platform Settings" />
          </div>
        </Panel>

        {/* User role breakdown */}
        <Panel title="User Roles" icon={<UsersIcon className="w-4 h-4" />}>
          {overview?.users && overview.users.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {overview.users.map((r) => (
                <div key={r.role} className="flex items-center justify-between px-4 py-2.5">
                  <span className="text-sm text-slate-700 capitalize">{r.role.replace(/_/g, ' ')}</span>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-slate-500">{r.active} active</span>
                    <span className="text-sm font-semibold text-slate-800 w-8 text-right">{r.count}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState title="No user data available" />
          )}
        </Panel>
      </div>

      {/* Approval review drawer */}
      {reviewRequest && (
        <OnboardingReviewDrawer
          request={reviewRequest}
          onClose={() => setReviewRequest(null)}
          onApproved={() => { setReviewRequest(null); refresh(); }}
          onRejected={() => { setReviewRequest(null); refresh(); }}
        />
      )}
    </div>
  );
};

// ─── Onboarding Review Drawer ────────────────────────────────────────────────

function OnboardingReviewDrawer({ request, onClose, onApproved, onRejected }: {
  request: RegistrationRequest;
  onClose: () => void;
  onApproved: () => void;
  onRejected: () => void;
}) {
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMsg, setActionMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [precheck, setPrecheck] = useState<PrecheckResult | null>(null);
  const [precheckLoading, setPrecheckLoading] = useState(true);

  // Run precheck on mount
  React.useEffect(() => {
    let cancelled = false;
    precheckRequest(request.id)
      .then((data) => { if (!cancelled) setPrecheck(data); })
      .catch(() => { if (!cancelled) setPrecheck(null); })
      .finally(() => { if (!cancelled) setPrecheckLoading(false); });
    return () => { cancelled = true; };
  }, [request.id]);

  const hasSlugConflict = precheck?.warnings.some((w) => w.type === 'slug_conflict') ?? false;
  const hasExistingUser = precheck?.warnings.some((w) => w.type === 'existing_user') ?? false;

  const handleApprove = async () => {
    if (hasSlugConflict) {
      if (!window.confirm(`Warning: slug conflict detected. Approval will fail with a 409 error. Resolve the slug conflict first.`)) return;
    }
    if (!window.confirm(`Approve "${request.company_name}"? This will create the organisation and Project Manager account.`)) return;
    setActionLoading(true);
    setActionMsg(null);
    try {
      const result = await approveRequest(request.id);
      const warnings = [];
      if (result.email_sent) warnings.push('Email sent');
      setActionMsg({ type: 'success', text: `Approved. Organisation created.${warnings.length ? ' ' + warnings.join(', ') + '.' : ''}` });
      setTimeout(onApproved, 1500);
    } catch (err: unknown) {
      setActionMsg({ type: 'error', text: err instanceof Error ? err.message : 'Failed to approve' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    setActionLoading(true);
    setActionMsg(null);
    try {
      await rejectRequest(request.id, rejectReason || undefined);
      setActionMsg({ type: 'success', text: 'Request rejected.' });
      setTimeout(onRejected, 1500);
    } catch (err: unknown) {
      setActionMsg({ type: 'error', text: err instanceof Error ? err.message : 'Failed to reject' });
    } finally {
      setActionLoading(false);
    }
  };

  const requestedModules = (request.requested_modules || []) as string[];

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white shadow-xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 shrink-0">
          <div className="min-w-0">
            <h2 className="text-sm font-bold text-slate-800">Review Onboarding Request</h2>
            <p className="text-xs text-slate-500">#{request.id} · Submitted {request.submitted_at ? new Date(request.submitted_at).toLocaleDateString() : request.created_at ? new Date(request.created_at).toLocaleDateString() : '—'}</p>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100" aria-label="Close">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Action feedback */}
        {actionMsg && (
          <div className={`px-4 py-2 text-xs font-medium ${actionMsg.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
            {actionMsg.text}
          </div>
        )}

        {/* Precheck warnings */}
        {precheckLoading && (
          <div className="px-4 py-2 bg-slate-50 border-b border-slate-200">
            <p className="text-xs text-slate-500">Running precheck...</p>
          </div>
        )}
        {!precheckLoading && precheck && precheck.warnings.length > 0 && (
          <div className="px-4 py-3 bg-amber-50 border-b border-amber-200 space-y-2">
            <p className="text-xs font-semibold text-amber-700">Pre-Approval Warnings</p>
            {precheck.warnings.map((w, i) => (
              <div key={i} className="text-xs text-amber-600">
                {w.type === 'slug_conflict' && (
                  <span>
                    <span className="font-medium">Slug conflict:</span> "{precheck.slug}" already belongs to
                    {w.existing_tenant && (
                      <> <span className="font-medium">{w.existing_tenant.name}</span> (ID: {w.existing_tenant.id}, status: {w.existing_tenant.status})</>
                    )}. Approval will be blocked until this is resolved.
                  </span>
                )}
                {w.type === 'existing_user' && (
                  <span>
                    <span className="font-medium">Existing user:</span> {w.message}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Request detail */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Organisation info */}
          <Section title="Organisation">
            <Row label="Name" value={request.company_name} />
            <Row label="Legal Name" value={request.legal_name} />
            <Row label="Country" value={request.country} />
            <Row label="Business Type" value={request.business_type} />
            <Row label="Main Crops" value={request.main_crops} />
            <Row label="Farmer Count" value={request.farmer_count} />
          </Section>

          {/* Contact info */}
          <Section title="Contact">
            <Row label="Contact Name" value={request.contact_name} />
            <Row label="Email" value={request.contact_email} />
            <Row label="Phone" value={request.contact_phone} />
          </Section>

          {/* Address */}
          <Section title="Address">
            <Row label="Province" value={request.province} />
            <Row label="District" value={request.district} />
            <Row label="Sector" value={request.sector} />
            <Row label="Cell" value={request.cell} />
            <Row label="Village" value={request.village} />
            <Row label="Operating Districts" value={request.operating_districts} />
          </Section>

          {/* Registration */}
          <Section title="Registration">
            <Row label="TIN" value={request.tin} />
            <Row label="Registration Number" value={request.registration_number} />
            <Row label="Domain" value={request.domain} />
            <Row label="Admin Domain" value={request.admin_domain} />
            <Row label="Website" value={request.website_url} />
          </Section>

          {/* Requested modules */}
          {requestedModules.length > 0 && (
            <Section title="Requested Modules">
              <div className="flex flex-wrap gap-1.5">
                {requestedModules.map((m) => (
                  <span key={m} className="px-2 py-0.5 text-xs font-medium bg-emerald-50 text-emerald-700 rounded-full border border-emerald-200">
                    {m.replace(/_/g, ' ')}
                  </span>
                ))}
              </div>
            </Section>
          )}

          {/* Notes */}
          {request.notes && (
            <Section title="Notes">
              <p className="text-sm text-slate-600">{request.notes}</p>
            </Section>
          )}
        </div>

        {/* Footer actions */}
        <div className="px-4 py-3 border-t border-slate-200 shrink-0 bg-slate-50 space-y-3">
          {showRejectForm ? (
            <div className="space-y-2">
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Reason for rejection (optional)"
                rows={2}
                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500/30"
              />
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleReject}
                  disabled={actionLoading}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                >
                  {actionLoading ? 'Processing...' : 'Confirm Reject'}
                </button>
                <button type="button" onClick={() => { setShowRejectForm(false); setRejectReason(''); }} className="text-sm text-slate-500 hover:text-slate-700">
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => setShowRejectForm(true)}
                disabled={actionLoading}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50 transition-colors"
              >
                <XCircleIcon className="w-4 h-4" />
                Reject
              </button>
              <button
                type="button"
                onClick={handleApprove}
                disabled={actionLoading}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors"
              >
                {actionLoading ? (
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <CheckCircleIcon className="w-4 h-4" />
                )}
                {actionLoading ? 'Processing...' : 'Approve'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────────

const COLOR_MAP: Record<string, { bg: string; text: string; border: string }> = {
  emerald: { bg: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-200' },
  blue: { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200' },
  amber: { bg: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-200' },
  slate: { bg: 'bg-slate-50', text: 'text-slate-600', border: 'border-slate-200' },
};

function KPICard({ icon, label, value, sub, color }: { icon: React.ReactNode; label: string; value: number; sub: string; color: string }) {
  const c = COLOR_MAP[color] || COLOR_MAP.slate;
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${c.bg} ${c.text}`}>{icon}</div>
        <div className="min-w-0">
          <p className="text-xs text-slate-500">{label}</p>
          <p className="text-xl font-bold text-slate-800">{value.toLocaleString()}</p>
          <p className="text-xs text-slate-400">{sub}</p>
        </div>
      </div>
    </div>
  );
}

function Panel({ title, icon, children }: { title: string; icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
        {icon && <span className="text-slate-400">{icon}</span>}
        <h2 className="text-sm font-semibold text-slate-700">{title}</h2>
      </div>
      <div>{children}</div>
    </div>
  );
}

function HealthRow({ label, healthy }: { label: string; healthy: boolean }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-slate-500">{label}</span>
      <span className={`flex items-center gap-1.5 font-medium ${healthy ? 'text-emerald-600' : 'text-red-600'}`}>
        <span className={`w-2 h-2 rounded-full ${healthy ? 'bg-emerald-500' : 'bg-red-500'}`} />
        {healthy ? 'Healthy' : 'Unhealthy'}
      </span>
    </div>
  );
}

function QuickLink({ to, label }: { to: string; label: string }) {
  return (
    <Link to={to} className="flex items-center justify-between px-3 py-2 rounded-lg text-sm text-slate-600 hover:text-slate-800 hover:bg-slate-50 transition-colors">
      {label}
      <span className="text-slate-300">&rsaquo;</span>
    </Link>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <div className="px-3 py-2 bg-slate-50 border-b border-slate-100">
        <h3 className="text-xs font-medium text-slate-500 uppercase tracking-wider">{title}</h3>
      </div>
      <div className="divide-y divide-slate-50">{children}</div>
    </div>
  );
}

function Row({ label, value }: { label: string; value?: string | number | null }) {
  if (!value) return null;
  return (
    <div className="flex items-center justify-between px-3 py-2 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-700 font-medium text-right max-w-[60%] truncate">{String(value)}</span>
    </div>
  );
}

export default PlatformOverviewPage;
